import React, { useState } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { Search, MapPin, Home, DollarSign } from 'lucide-react';

interface SearchFilters {
  searchQuery: string;
  city: string;
  minPrice: string;
  maxPrice: string;
  beds: string;
  baths: string;
  propertyType: string;
}

interface PropertySearchProps {
  onSearch: (filters: SearchFilters) => void;
  loading?: boolean;
}

const PropertySearch: React.FC<PropertySearchProps> = ({ onSearch, loading }) => {
  const [filters, setFilters] = useState<SearchFilters>({
    searchQuery: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    beds: 'any',
    baths: 'any',
    propertyType: 'any'
  });

  const handleInputChange = (field: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(filters);
  };

  const handleReset = () => {
    setFilters({
      searchQuery: '',
      city: '',
      minPrice: '',
      maxPrice: '',
      beds: 'any',
      baths: 'any',
      propertyType: 'any'
    });
    onSearch({
      searchQuery: '',
      city: '',
      minPrice: '',
      maxPrice: '',
      beds: 'any',
      baths: 'any',
      propertyType: 'any'
    });
  };

  return (
    <Form onSubmit={handleSubmit} className="property-search-form">
      <Row className="g-3">
        <Col md={12}>
          <InputGroup size="lg">
            <InputGroup.Text>
              <Search size={20} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by address, neighborhood, or MLS#..."
              value={filters.searchQuery}
              onChange={(e) => handleInputChange('searchQuery', e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <MapPin size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="City"
              value={filters.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>
              <DollarSign size={16} />
            </InputGroup.Text>
            <Form.Control
              type="number"
              placeholder="Min Price"
              value={filters.minPrice}
              onChange={(e) => handleInputChange('minPrice', e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>
              <DollarSign size={16} />
            </InputGroup.Text>
            <Form.Control
              type="number"
              placeholder="Max Price"
              value={filters.maxPrice}
              onChange={(e) => handleInputChange('maxPrice', e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col md={2}>
          <Form.Select
            value={filters.beds}
            onChange={(e) => handleInputChange('beds', e.target.value)}
          >
            <option value="any">Beds</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
            <option value="5">5+</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select
            value={filters.baths}
            onChange={(e) => handleInputChange('baths', e.target.value)}
          >
            <option value="any">Baths</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </Form.Select>
        </Col>

        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <Home size={16} />
            </InputGroup.Text>
            <Form.Select
              value={filters.propertyType}
              onChange={(e) => handleInputChange('propertyType', e.target.value)}
            >
              <option value="any">Property Type</option>
              <option value="single_family">Single Family</option>
              <option value="condo">Condo</option>
              <option value="townhouse">Townhouse</option>
              <option value="multi_family">Multi-Family</option>
              <option value="land">Land</option>
              <option value="commercial">Commercial</option>
            </Form.Select>
          </InputGroup>
        </Col>

        <Col md={9} className="d-flex gap-2">
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading}
            className="px-5"
          >
            {loading ? 'Searching...' : 'Search Properties'}
          </Button>
          <Button 
            variant="outline-secondary" 
            type="button"
            onClick={handleReset}
          >
            Reset Filters
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default PropertySearch;